import React, { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import MiniLineChart from "./MiniLineChart";
import CumulatPeOre from "./CumulatPeOre";
import { METRICS, sel } from "./configuratorMetrics";

const SUBTABS = [["serie", "Serie temporala"], ["cumulat", "Cumulat pe ore"]];

const PERIODS = [
  ["ora", "Ore (azi)"],
  ["zi", "Zile (ultimele 30)"],
  ["saptamana", "Saptamani"],
  ["luna", "Luni"],
  ["trimestru", "Trimestre"],
  ["semestru", "Semestre"],
  ["an", "Ani"],
];

export default function RapoarteConfigurator() {
  const [tab, setTab] = useState("serie");
  return (
    <div data-testid="rapoarte-configurator">
      <div className="mb-4 inline-flex rounded bg-slate-100 p-1 text-sm">
        {SUBTABS.map(([v, l]) => (
          <button key={v} data-testid={`subtab-${v}`} onClick={() => setTab(v)}
            className={`rounded px-3 py-1 ${tab === v ? "bg-white font-medium shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>{l}</button>
        ))}
      </div>
      {tab === "serie" ? <SerieTemporala /> : <CumulatPeOre />}
    </div>
  );
}

function SerieTemporala() {
  const [f, setF] = useState({ country: "all", period: "zi", date_from: "", date_to: "" });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if ((f.date_from && !f.date_to) || (!f.date_from && f.date_to)) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/api/configurator/evolutive", { params: f });
      setData(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Eroare la incarcare");
    } finally {
      setLoading(false);
    }
  }, [f]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const countries = data?.available_countries || [];

  return (
    <div data-testid="serie-temporala">
      <div className="mb-5 flex flex-wrap items-center justify-end gap-3 text-sm">
        <label className="flex items-center gap-2">Tara
          <select data-testid="filter-country" className={sel} value={f.country} onChange={set("country")}>
            <option value="all">Toate</option>
            <option value="RO">Romania</option>
            <option value="EXT">Extern</option>
            <option value="UNK">Necunoscut</option>
            {countries.filter((c) => c !== "RO").map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2">Perioada:
          <select data-testid="filter-period" className={sel} value={f.period} onChange={set("period")} disabled={!!(f.date_from && f.date_to)}>
            {PERIODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2">De la
          <input data-testid="filter-date-from" type="date" className={sel} value={f.date_from} onChange={set("date_from")} />
        </label>
        <label className="flex items-center gap-2">Pana la
          <input data-testid="filter-date-to" type="date" className={sel} value={f.date_to} onChange={set("date_to")} />
        </label>
      </div>

      {loading && <div data-testid="evolutive-loading" className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Se incarca...</div>}
      {error && <div data-testid="evolutive-error" className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      {data && !error && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {METRICS.map(([key, label, color]) => {
            const values = data.series?.[key] || [];
            const total = values.reduce((a, b) => a + (b || 0), 0);
            return (
              <div key={key} data-testid={`chart-${key}`} className="rounded border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-2 flex items-center justify-between text-sm">
                  <span className="font-medium text-slate-700">{label}</span>
                  <span className="text-xs text-slate-500">Total: <b data-testid={`total-${key}`} className="text-slate-800">{total}</b></span>
                </div>
                <MiniLineChart labels={data.labels || []} values={values} color={color} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
